import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import fxmlogo from "../../../assets/icons/fxmedia-logo-white.png";
import refreshIcon from "../../../assets/classroom/images/classroom_bg-refresh-button.png";
import showMenuIcon from "../../../assets/classroom/images/classroom_bg-show-menu.png";
import IntroductionVideo from "../introduction-video/introductionVideo";
import ChromaKeyVideo from "../../../component/chroma-key/chromakey";
import MenuClassroom from "../../../component/menu/menu-classroom";
import BubbleChat from "../../../component/bubble-chat/bubbleChat";
import { FaPlay } from "react-icons/fa6";
import chatMicrophone from "../../../assets/classroom/images/classroom_bg-mic-button.png";
import classroomVideoConfig, {
  getNextStep,
} from "../../../component/video-config/classroomVideoConfig";
import { login, getAuthToken } from "../../../auth/auth";
import useAvatarVideos from "../../../component/video-config/useAvatarVideos";
import VoiceInput from "../../../component/audio/voiceInput";
import packageJson from "../../../../package.json";
import ConnectionTest from "../../../auth/connectionTestv2";
import DefaultValue from "../../../component/default-value/defaultValue";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const ClassroomHomePage = () => {
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const { avatar } = useParams();
  const [selectedAvatar, setSelectedAvatar] = useState("Emily");
  const prevAvatarRef = useRef(null);
  const [currentStep, setCurrentStep] = useState(() => {
    return parseInt(sessionStorage.getItem("currentStep")) || 0;
  });
  const [username, setUsername] = useState(() => {
    return sessionStorage.getItem("username") || "";
  });
  // const [isIntroComplete, setIsIntroComplete] = useState(() => {
  //   return sessionStorage.getItem("isIntroComplete") === "true" || false;
  // });
  const [isIntroComplete, setIsIntroComplete] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState(() => {
    const savedMessages = sessionStorage.getItem("messages");
    return savedMessages ? JSON.parse(savedMessages) : [];
  });
  const [messageInput, setMessageInput] = useState("");
  const [audioPlayer, setAudioPlayer] = useState(null);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [currentMessage, setCurrentMessage] = useState("");
  const [authToken, setAuthToken] = useState("");
  const { idleVideo, talkVideo } = useAvatarVideos();
  const [showVoiceInput, setShowVoiceInput] = useState(false);
  const [mode, setMode] = useState("silent");
  const [isVideoLoading, setIsVideoLoading] = useState(true);
  const [aiResponse, setAiResponse] = useState("");
  const [manualPlay, setManualPlay] = useState(false);

  const language = sessionStorage.getItem("language") || "EN";
  const passphrase = process.env.REACT_APP_CLASSROOM_KEY;
  const configId = sessionStorage.getItem("id") || 1;

  DefaultValue();

  useEffect(() => {
    const performLogin = async () => {
      try {
        const token = await login();
        setAuthToken(token);
      } catch (error) {
        console.error("Failed to login:", error);
      }
    };

    performLogin();
  }, [authToken]);

  useEffect(() => {
    if (avatar) {
      setSelectedAvatar(avatar);
    }
    console.log("Avatar:", avatar);
  }, [avatar]);

  useEffect(() => {
    if (prevAvatarRef.current !== null && prevAvatarRef.current !== selectedAvatar) {
      setMessages([]);
      sessionStorage.removeItem("messages");
    }
    prevAvatarRef.current = selectedAvatar;
  }, [selectedAvatar]);

  useEffect(() => {
    const { video, message } =
      classroomVideoConfig[selectedAvatar]?.[language][currentStep] || {};
    setCurrentVideo(video);
    setCurrentMessage(message);
  }, [currentStep, selectedAvatar, language]);

  // Save state to sessionStorage whenever it changes
  useEffect(() => {
    // sessionStorage.setItem("currentStep", currentStep);
    sessionStorage.setItem("username", username);
    sessionStorage.setItem("messages", JSON.stringify(messages));
    sessionStorage.setItem("isIntroComplete", isIntroComplete);
  }, [currentStep, username, messages, isIntroComplete]);

  // Map introductory messages based on avatar and language
  const introMessage =
    classroomVideoConfig[selectedAvatar]?.[language]?.map(
      (item) => item.introMessage
    ) || [];

  const handleVideoEnd = useCallback(() => {
    setTimeout(() => {
      setCurrentStep((prevStep) => getNextStep(prevStep, selectedAvatar));
      setCurrentVideo(idleVideo);
    }, 500);
  }, [selectedAvatar, idleVideo]);

  const handleUsernameSubmit = (name) => {
    setUsername(name);
    setIsIntroComplete(true);
    setCurrentVideo(idleVideo);
    setIsIntroComplete(true);
  };

  const toggleMenuVisibility = () => {
    setIsMenuVisible((prevVisibility) => !prevVisibility);
  };

  const closeMenu = () => {
    setIsMenuVisible(false);
  };

  const handleRefreshButton = () => {
    const language = localStorage.getItem("language") || "English";
    const confirmationMessage =
      language === "Indonesia"
        ? "Apakah Anda yakin ingin menyegarkan halaman?"
        : "Are you sure you want to refresh the page?";

    // eslint-disable-next-line no-restricted-globals
    const userConfirmed = confirm(confirmationMessage);

    if (userConfirmed) {
      sessionStorage.clear();
      window.location.href = "/classroom";
    }
  };

  const playAudio = useCallback(
    (audioUrl) => {
      const fullAudioUrl = `${BASE_URL}${audioUrl}`;
      setCurrentVideo(talkVideo);
      console.log("Playing audio:", fullAudioUrl);

      if (audioPlayer) {
        audioPlayer.pause();
        audioPlayer.src = "";
      }

      const audio = new Audio(fullAudioUrl);
      audio.preload = "auto";
      setAudioPlayer(audio);

      audio.oncanplaythrough = () => {
        audio.play().catch((error) => {
          console.error("Failed to play audio:", error);
        });
      };

      audio.onended = () => {
        console.log("Audio playback ended");
        setMode((prevMode) => (prevMode === "talk" ? "listen" : "silent"));
        setCurrentVideo(idleVideo);
      };
    },
    [audioPlayer, idleVideo, talkVideo]
  );

  const handleSendMessage = useCallback(
    async (inputMessage) => {
      if (inputMessage.trim()) {
        const userMessage = {
          type: "user",
          username: username,
          text: inputMessage,
        };
        setMessages((prevMessages) => [...prevMessages, userMessage]);
        setMessageInput("");
        setIsLoading(true);

        try {
          const token = getAuthToken();
          const response = await axios.post(
            `${BASE_URL}/api/chat?passphrase=${passphrase}&config_id=${configId}&app_id=1`,
            {
              prompt: inputMessage,
              use_audio: "true",
              use_expression: "true",
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          const aiResponse = response.data.data.text;
          const audioUrl = response.data.data.audioHyperlink;

          setMessages((prevMessages) => [
            ...prevMessages,
            { type: "ai", text: aiResponse },
          ]);

          setAiResponse(aiResponse);

          if (audioUrl) {
            playAudio(audioUrl);
            setMode("talk");
          }

          setIsLoading(false);
        } catch (error) {
          console.error("Failed to send message:", error);
          setMode("silent");
          setIsLoading(false);
        }
      }
    },
    [username, talkVideo, configId, passphrase, playAudio]
  );

  const toggleVoiceInput = () => {
    setShowVoiceInput((prev) => !prev);
  };

  const closeVoiceInput = () => {
    // eslint-disable-next-line no-restricted-globals
    const userConfirmed = confirm("Are you sure you want to exit?");
    if (userConfirmed) {
      setShowVoiceInput(false);
      setMode("silent");
    }
  };

  const memoizedVideo = useMemo(
    () => (
      <ChromaKeyVideo
        className="avatar-video-render"
        src={currentVideo}
        chromaColor={{ r: 0, g: 255, b: 0 }}
        loop={currentVideo === idleVideo || currentVideo === talkVideo}
        onEnded={handleVideoEnd}
        muted={currentVideo === talkVideo}
        playing={manualPlay}
      />
    ),
    [currentVideo, idleVideo, talkVideo, handleVideoEnd, manualPlay]
  );

  const isAvatarInVideoUrl = (videoUrl, avatarName) => {
    // Bypass this check: backend video URLs might not contain the avatar's name,
    // which was causing an infinite reload loop.
    return !!videoUrl;
  };

  useEffect(() => {
    if (currentVideo) {
      if (!isAvatarInVideoUrl(currentVideo, selectedAvatar)) {
        setIsVideoLoading(true);
      } else {
        const videoElement = document.createElement("video");
        videoElement.src = currentVideo;
        videoElement.preload = "auto";

        videoElement.onloadeddata = () => {
          setIsVideoLoading(false);
        };

        videoElement.onerror = () => {
          console.error("Failed to load video");
          setIsVideoLoading(false);
        };
      }
    }

    const timeoutId = setTimeout(() => {
      if (isVideoLoading) {
        console.log("Reloading page due to video loading issue");
        window.location.reload();
      }
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [currentVideo, selectedAvatar, isVideoLoading]);

  const handlePlayVideoManually = () => {
    setManualPlay(true);
    document.querySelectorAll("video").forEach((v) => {
      v.play().catch(() => {});
    });
  };

  return (
    <div className="classroom__homepage">
      <img src={fxmlogo} alt="fxmlogo" className="logo" />
      <div className="app__info">
        <div className="app__version">
          <p>{packageJson.version}</p>
        </div>
        <ConnectionTest passphrase={passphrase} configId={configId} micOn={showVoiceInput && mode === "listen"} />
      </div>
      {isMenuVisible && (
        <MenuClassroom
          closeMenu={closeMenu}
          messages={messages}
          userName={username}
        />
      )}
      {!isMenuVisible && (
        <div className="navbar">
          <div className="refresh-menu" onClick={handleRefreshButton}>
            <img src={refreshIcon} alt="Refresh" />
          </div>
          <div className="show-menu" onClick={toggleMenuVisibility}>
            <img src={showMenuIcon} alt="Show Menu" />
          </div>
        </div>
      )}

      {!manualPlay && (
        <div className="play-video--overlay">
          <button className="play-video--button" onClick={handlePlayVideoManually}>
            Click to start
          </button>
        </div>
      )}

      <div className="classroom__homepage-content">
        {isVideoLoading ? (
          <div className="loading-screen">
            <div className="loading-text">Loading...</div>
          </div>
        ) : (
          memoizedVideo
        )}

        {!isIntroComplete ? (
          <div className="classroom__homepage-intro--content">
            <IntroductionVideo
              currentStep={currentStep}
              setCurrentStep={setCurrentStep}
              setUsername={handleUsernameSubmit}
              handleSendMessage={handleSendMessage}
              introMessage={introMessage}
            />
          </div>
        ) : (
          !showVoiceInput && (
            <div className="classroom__homepage-chat">
              <div className="classroom__bubble-chat--container">
                <BubbleChat
                  messages={messages}
                  isLoading={isLoading}
                  userName={username}
                />
              </div>
              <div className="classroom__input-message">
                <div className="classroom__microphone-container">
                  <img
                    src={chatMicrophone}
                    alt="Microphone"
                    className="chat-microphone"
                    onClick={toggleVoiceInput}
                  />
                </div>
                <input
                  type="text"
                  value={messageInput}
                  placeholder="Type your message..."
                  onChange={(e) => setMessageInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleSendMessage(messageInput);
                    }
                  }}
                />
                <div
                  className="send-button"
                  onClick={() => handleSendMessage(messageInput)}
                >
                  <FaPlay />
                </div>
              </div>
            </div>
          )
        )}
        {showVoiceInput && (
          <VoiceInput
            closeVoiceInput={closeVoiceInput}
            setCurrentVideo={setCurrentVideo}
            idleVideo={idleVideo}
            talkVideo={talkVideo}
            handleSendMessage={handleSendMessage}
            setMode={setMode}
            mode={mode}
            aiResponse={aiResponse}
          />
        )}
      </div>
    </div>
  );
};

export default ClassroomHomePage;
